import { type ReactNode } from 'react'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  CardButton,
  CardSubtitle,
  CardItemContent,
  CardItemIcon,
  CardTitle,
} from '@/components/Card'
import { dateFormatters } from '@/utils/date-util'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import {
  AutoSkeleton,
  SkeletonList,
  useIsSkeleton,
} from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import styles from './ModalResponsesSection.module.css'
import type { QuestionsDisplayViewModel } from '@/features/jobs/details/QuestionsDisplayViewModel'
import { observer } from 'mobx-react-lite'
import { getQuestionAnswerAsText } from '@/features/jobs/questions/question-display'
import type { PressEventHandler } from '@/infra/prop-types'

export interface ModalResponsesSectionProps {
  headline: string
  responseCount: number
  submittedDate: Date
  submitterDisplayName: string
  onPress?: PressEventHandler
  buttonTestId?: string
}

/**
 * Section for showing the questionnaire responses summary.
 */
export function ModalResponsesSection({
  headline,
  responseCount,
  submittedDate,
  submitterDisplayName,
  onPress,
  buttonTestId,
}: ModalResponsesSectionProps) {
  return (
    <PageSection>
      <SectionHeadline>{headline}</SectionHeadline>
      <CardButton onPress={onPress} testId={buttonTestId}>
        <CardItemContent isInteractive icon={<CardItemIcon name="file" />}>
          <CardTitle>{responseCount} responses captured</CardTitle>
          <CardSubtitle variant="label">
            Submitted by {submitterDisplayName} on{' '}
            {dateFormatters.dateTimeDefault.format(submittedDate)}
          </CardSubtitle>
        </CardItemContent>
      </CardButton>
    </PageSection>
  )
}

interface ModalResponsesDialogProps {
  headline: string
  responseCount: number
  submittedDate: Date
  submitterDisplayName: string
  children?: ReactNode
  isOpen: boolean
  onClose?: () => unknown
  testId?: string
  closeButtonTestId?: string
}

/**
 * Dialog for questionnaire responses.
 */
export function ModalResponsesDialog({
  headline,
  submittedDate,
  submitterDisplayName,
  children,
  isOpen,
  onClose,
  testId,
  closeButtonTestId,
}: ModalResponsesDialogProps) {
  return (
    <ModalDialog size="lg" isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader textAlign="left">
        <ModalDialogTitle>{headline}</ModalDialogTitle>
        <ModalDialogDescription>
          All responses submitted by {submitterDisplayName} on{' '}
          {dateFormatters.dateTimeDefault.format(submittedDate)}
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <ul>{children}</ul>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Close"
            variant="outline"
            onPress={onClose}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ModalResponsesDialogResponseItemProps {
  question?: string
  answer?: string
  isSkeleton?: boolean
  questionTestId?: string
  answerTestId?: string
}

/**
 * Component for a response to a question.
 */
export function ModalResponsesDialogResponseItem({
  question,
  answer,
  isSkeleton,
  questionTestId,
  answerTestId,
}: ModalResponsesDialogResponseItemProps) {
  const { vary, index } = useSkeletonListContext()
  isSkeleton = useIsSkeleton(isSkeleton)

  return (
    <li className={styles.responseItem}>
      <p className="text-body-base pb-sm" data-test={questionTestId}>
        <AutoSkeleton
          isSkeleton={isSkeleton}
          characters={vary(index % 3 === 0 ? 40 : 25)}
        >
          {question}
        </AutoSkeleton>
      </p>
      <p className="text-headline-sm" data-test={answerTestId}>
        <AutoSkeleton
          isSkeleton={isSkeleton}
          characters={vary(index % 3 === 0 ? 60 : 20)}
        >
          {answer}
        </AutoSkeleton>
      </p>
    </li>
  )
}

interface ModalResponsesSectionContainerProps {
  vm: QuestionsDisplayViewModel
  sectionHeadline: string
  dialogHeadline: string
}

/**
 * Modal Responses Section Container
 */
export const ModalResponsesSectionContainer = observer(
  function ModalResponsesSectionContainer({
    vm,
    sectionHeadline,
    dialogHeadline,
  }: ModalResponsesSectionContainerProps) {
    const submittedDate = vm.job.submittedAt
    const responsesCount = vm.questions?.length ?? vm.job.answers.length

    // Not sure where we'd find info about it being someone else?
    // Can always leave out this detail.
    const submitterDisplayName = 'you'
    return (
      <>
        <ModalResponsesSection
          headline={sectionHeadline}
          responseCount={responsesCount}
          submittedDate={submittedDate}
          submitterDisplayName={submitterDisplayName}
          onPress={vm.modal.show}
          buttonTestId="show-responses-modal-button"
        />
        <ModalResponsesDialog
          headline={dialogHeadline}
          responseCount={responsesCount}
          submittedDate={submittedDate}
          submitterDisplayName={submitterDisplayName}
          isOpen={vm.modal.showing}
          onClose={vm.modal.close}
          closeButtonTestId="responses-modal-close-button"
        >
          {!vm.questions ? (
            <SkeletonList count={responsesCount}>
              <ModalResponsesDialogResponseItem />
            </SkeletonList>
          ) : (
            vm.questions.map((question) => (
              <ModalResponsesDialogResponseItem
                key={question.id}
                question={question.title}
                answer={getQuestionAnswerAsText(question)}
                questionTestId={`response-item-question-${question.id}`}
                answerTestId={`response-item-answer-${question.id}`}
              />
            ))
          )}
        </ModalResponsesDialog>
      </>
    )
  },
)
